import React from 'react';
import { Box, Typography, Button, TextField, Link } from '@mui/material';

/**
 * Reusable component for displaying Position, Grade & Roles information
 * Used in PersonalDetails and EmploymentHistory components
 * Supports both structured (Position/Grade/Roles) and custom text classification
 */
const PositionGradeRolesBox = ({
  position,
  grade,
  roles,
  onSelectClick,
  useCustomClassification = false,
  onToggleCustomClassification,
  customClassification = '',
  onCustomClassificationChange,
  readonly = false,
  sx = {}
}) => {
  return (
    <Box sx={{ mb: 2, ...sx }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Position, Grade & Roles
        </Typography>
        {onToggleCustomClassification && !readonly && (
          <Link
            component="button"
            type="button"
            variant="caption"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleCustomClassification();
            }}
            sx={{
              cursor: 'pointer',
              textDecoration: 'none',
              border: 'none',
              background: 'none',
              padding: 0,
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            {useCustomClassification ? 'Use Structured Classification' : 'Use Custom Classification'}
          </Link>
        )}
      </Box>

      {useCustomClassification ? (
        <TextField
          fullWidth
          variant="outlined"
          value={customClassification}
          onChange={(e) => onCustomClassificationChange?.(e.target.value)}
          placeholder="Enter custom position, grade, and roles classification"
          size="small"
          disabled={readonly}
        />
      ) : (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          p: 2,
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          backgroundColor: '#f5f5f5'
        }}>
          <Typography variant="body2">
            <strong>Position:</strong> {position?.name || 'Not specified'}
          </Typography>
          <Typography variant="body2">
            <strong>Grade:</strong> {grade?.name || 'Not specified'}
          </Typography>
          <Typography variant="body2">
            <strong>Roles:</strong> {roles?.length ? roles.map(role => role.name).join(', ') : 'No roles assigned'}
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={onSelectClick}
            sx={{ mt: 1, alignSelf: 'flex-start' }}
            disabled={readonly}
          >
            Select Grade, Position & Roles
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default PositionGradeRolesBox;
